/* Acumen Community storefront widget — section-aware build.
 *
 * The community page is composed of THREE theme sections. This script only
 * fills the DYNAMIC parts (live UGC from the API) into mount points the
 * sections render; the sections own layout, copy, tab config and the video
 * app-block slot.
 *
 * Mount points (any subset may be present):
 *   [data-acumen-hero]        → hero stat numbers
 *   [data-acumen-ambassadors] → ambassador slider (rail + arrows)
 *   [data-acumen-feed]        → scenario tabs + photo masonry (video tab = app block)
 *
 * API: https://ugc.acumen-camera.com/api-community , /api-ambassadors
 */
(function () {
  if (window.__acumenCommunityInit) return; // guard against multiple section script tags
  window.__acumenCommunityInit = true;

  const BASE = "https://ugc.acumen-camera.com";

  const SCENARIO_BLURB = {
    daily: "Commute incidents, parked-mode catches, near-misses. The boring stuff that pays for the camera.",
    rv: "Long-haul trips, boondocking, life-on-wheels footage from owners who live in their vehicles.",
    adventure: "Off-road, snow-line, animal crossings, weather. Where the cameras and the drivers get tested.",
    event: "Crashes, hit-and-runs, hailstorms — clips that became insurance claims and court evidence.",
    install: "Tutorials, fuse-tap tips, cable runs, and the rare 'how I screwed this up' confessional.",
  };

  const TONE = {
    daily:     { a: "#3A4756", b: "#1F2730" },
    rv:        { a: "#A87A4E", b: "#5C3E22" },
    adventure: { a: "#3F5D45", b: "#1B2A21" },
    event:     { a: "#7A3C46", b: "#34161D" },
    install:   { a: "#5C5650", b: "#2E2A26" },
  };

  // Shared live data (fetched once, consumed by every mounted section).
  const store = { data: null, ambassadors: null, error: null, ready: false };
  const subscribers = [];
  function onReady(fn) { store.ready ? fn() : subscribers.push(fn); }

  // ─── Style + font injection ─────────────────────────────
  function injectFontLink() {
    if (document.getElementById("acumen-community-font")) return;
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect"; pre1.href = "https://fonts.googleapis.com";
    document.head.appendChild(pre1);
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect"; pre2.href = "https://fonts.gstatic.com"; pre2.crossOrigin = "anonymous";
    document.head.appendChild(pre2);
    const link = document.createElement("link");
    link.id = "acumen-community-font"; link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);
  }

  function injectStyles() {
    if (document.getElementById("acumen-community-styles")) return;
    const css = `
.ac-community {
  --bg:#FFFFFF; --bg-soft:#F2F4F6; --bg-tint:#EDF2F5; --bg-card:#FFFFFF;
  --ink:#0E1116; --ink-soft:#2A2F38; --muted:#6B7280;
  --line:#E5E8EC; --line-strong:#CFD5DC;
  --accent:#19A6DC; --accent-soft:#D7EFFA; --accent-deep:#0E7FB0;
  --navy:#0F2542; --navy-soft:#1A3358;
  --shadow-sm:0 1px 2px rgba(15,37,66,.05),0 1px 1px rgba(15,37,66,.03);
  --shadow-md:0 6px 18px rgba(15,37,66,.08),0 2px 6px rgba(15,37,66,.05);
  --radius:10px; --radius-lg:18px;
  --font-sans:"Roboto","Helvetica Neue",Helvetica,Arial,sans-serif;
  --font-mono:"Roboto","Helvetica Neue",Helvetica,Arial,sans-serif;
  font-family:var(--font-sans); color:var(--ink); background:var(--bg);
  font-size:14px; line-height:1.5; -webkit-font-smoothing:antialiased;
}
.ac-community *,.ac-community *::before,.ac-community *::after{box-sizing:border-box;}
.ac-community a{color:inherit;text-decoration:none;}
.ac-community button{font-family:inherit;cursor:pointer;}
.ac-community .ac-shell{max-width:1320px;margin:0 auto;padding:0 40px;}
.ac-community .ac-mono{font-family:var(--font-mono);font-size:11px;letter-spacing:.04em;text-transform:uppercase;}

/* Hero */
.ac-community .ac-hero{padding:56px 0 28px;}
.ac-community .ac-hero-grid{display:grid;grid-template-columns:1.4fr 1fr;gap:64px;align-items:end;}
.ac-community .ac-eyebrow{display:inline-flex;align-items:center;gap:8px;color:var(--accent);font-size:13px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;}
.ac-community .ac-eyebrow::before{content:"";width:6px;height:6px;border-radius:50%;background:var(--accent);}
.ac-community .ac-hero h1{font-weight:800;font-size:clamp(42px,5.6vw,72px);line-height:1.04;letter-spacing:-.02em;margin:14px 0 0;color:var(--ink);text-wrap:balance;}
.ac-community .ac-hero h1 em{color:var(--accent);font-style:normal;font-weight:800;}
.ac-community .ac-hero-meta{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;padding-bottom:6px;}
.ac-community .ac-stat-num{font-weight:800;font-size:40px;line-height:1;letter-spacing:-.02em;color:var(--ink);}
.ac-community .ac-stat-label{color:var(--muted);font-size:12px;margin-top:8px;font-weight:500;}

/* Ambassador rail (slider) */
.ac-community .ac-amb-section{margin-top:48px;border-top:1px solid var(--line);border-bottom:1px solid var(--line);padding:32px 0 36px;}
.ac-community .ac-amb-head{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:24px;gap:24px;flex-wrap:wrap;}
.ac-community .ac-amb-title{font-weight:800;font-size:32px;line-height:1.05;margin:0;letter-spacing:-.02em;}
.ac-community .ac-amb-title em{color:var(--accent);font-style:normal;font-weight:800;}
.ac-community .ac-amb-sub{color:var(--muted);font-size:13.5px;margin-top:8px;max-width:540px;line-height:1.55;}
.ac-community .ac-amb-rail{display:flex;gap:20px;overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;padding-bottom:6px;-webkit-overflow-scrolling:touch;scrollbar-width:thin;}
.ac-community .ac-amb-rail::-webkit-scrollbar{height:6px;}
.ac-community .ac-amb-rail::-webkit-scrollbar-thumb{background:var(--line-strong);border-radius:99px;}
.ac-community .ac-amb-rail::-webkit-scrollbar-track{background:transparent;}
.ac-community .ac-amb-rail .ac-amb-card{flex:0 0 300px;scroll-snap-align:start;}
.ac-community .ac-amb-nav{display:inline-flex;gap:8px;}
.ac-community .ac-amb-nav button{width:36px;height:36px;border-radius:50%;border:1px solid var(--line);background:var(--bg-card);color:var(--ink);display:grid;place-items:center;transition:border-color .15s ease,color .15s ease,opacity .15s ease;}
.ac-community .ac-amb-nav button:hover:not(:disabled){border-color:var(--accent);color:var(--accent);}
.ac-community .ac-amb-nav button:disabled{opacity:.3;cursor:default;}
.ac-community .ac-amb-nav button svg{width:16px;height:16px;}
.ac-community .ac-amb-card{background:var(--bg-card);border:1px solid var(--line);border-radius:var(--radius);padding:18px;display:flex;flex-direction:column;gap:14px;transition:border-color .15s ease,transform .15s ease;}
.ac-community .ac-amb-card:hover{border-color:var(--line-strong);transform:translateY(-2px);}
.ac-community .ac-amb-portrait{position:relative;aspect-ratio:4/3;border-radius:8px;overflow:hidden;background:var(--bg-soft);}
.ac-community .ac-amb-portrait img{width:100%;height:100%;object-fit:cover;display:block;}
.ac-community .ac-amb-badge-tag{position:absolute;top:10px;left:10px;background:rgba(255,255,255,.95);color:var(--ink);font-weight:700;font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;padding:4px 10px;border-radius:999px;display:inline-flex;align-items:center;gap:5px;box-shadow:var(--shadow-sm);}
.ac-community .ac-amb-badge-tag::before{content:"";width:5px;height:5px;border-radius:50%;background:var(--accent);}
.ac-community .ac-amb-name{font-weight:800;font-size:18px;line-height:1.2;margin:0;letter-spacing:-.01em;}
.ac-community .ac-amb-role{color:var(--accent);font-weight:700;font-size:11px;letter-spacing:.06em;text-transform:uppercase;margin-top:4px;display:block;}
.ac-community .ac-amb-quote{font-weight:500;font-size:13.5px;color:var(--ink-soft);line-height:1.55;margin:0;text-wrap:pretty;}
.ac-community .ac-amb-quote::before{content:"\\201C";color:var(--accent);margin-right:2px;font-weight:700;}
.ac-community .ac-amb-quote::after{content:"\\201D";color:var(--accent);margin-left:2px;font-weight:700;}
.ac-community .ac-amb-meta{display:flex;flex-direction:column;gap:4px;font-size:12.5px;color:var(--muted);margin-top:auto;}
.ac-community .ac-amb-meta .row{display:flex;justify-content:space-between;align-items:baseline;}
.ac-community .ac-amb-meta .k{font-weight:700;font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:var(--muted);}
.ac-community .ac-amb-meta .v{color:var(--ink-soft);}
.ac-community .ac-amb-foot{display:flex;align-items:center;gap:10px;padding-top:12px;margin-top:4px;border-top:1px solid var(--line);}
.ac-community .ac-amb-foot .clips{font-weight:500;font-size:12px;color:var(--muted);}
.ac-community .ac-amb-foot .follow{margin-left:auto;height:30px;padding:0 14px;border-radius:999px;background:transparent;color:var(--accent);border:1px solid var(--line-strong);font-size:11.5px;font-weight:700;line-height:1;letter-spacing:.02em;display:inline-flex;align-items:center;gap:5px;transition:background .15s ease,border-color .15s ease,color .15s ease;}
.ac-community .ac-amb-foot .follow svg{width:13px;height:13px;}
.ac-community .ac-amb-foot .follow:hover{background:var(--accent);border-color:var(--accent);color:#fff;}
.ac-community .ac-amb-posts{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-top:2px;}
.ac-community .ac-amb-posts a{position:relative;display:block;aspect-ratio:1;border-radius:6px;overflow:hidden;background:var(--bg-soft);border:1px solid var(--line);}
.ac-community .ac-amb-posts img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .2s ease;}
.ac-community .ac-amb-posts a:hover img{transform:scale(1.06);}
.ac-community .ac-amb-posts .ac-amb-post-vid{position:absolute;right:5px;bottom:5px;width:16px;height:16px;border-radius:50%;background:rgba(255,255,255,.92);color:var(--ink);display:grid;place-items:center;box-shadow:var(--shadow-sm);}
.ac-community .ac-amb-posts .ac-amb-post-vid svg{width:9px;height:9px;}

/* Primary scenario tabs */
.ac-community .ac-tabs-row{display:flex;align-items:flex-end;justify-content:space-between;border-bottom:1px solid var(--line);margin-top:56px;gap:24px;flex-wrap:wrap;}
.ac-community .ac-tabs{display:flex;gap:4px;flex-wrap:wrap;}
.ac-community .ac-tab{display:inline-flex;align-items:baseline;gap:8px;padding:18px 20px 20px;background:transparent;border:none;color:var(--muted);font-weight:800;font-size:20px;letter-spacing:-.015em;border-bottom:2px solid transparent;margin-bottom:-1px;}
.ac-community .ac-tab .count{font-weight:600;font-size:11px;color:var(--muted);}
.ac-community .ac-tab.is-active{color:var(--ink);border-bottom-color:var(--accent);}
.ac-community .ac-tab.is-active .count{color:var(--accent);}
.ac-community .ac-tab:hover:not(.is-active){color:var(--ink-soft);}

/* Secondary media-type sub-tabs */
.ac-community .ac-sub-tabs-row{display:flex;align-items:center;justify-content:space-between;padding:22px 0 6px;gap:24px;flex-wrap:wrap;}
.ac-community .ac-sub-tabs-context{display:inline-flex;align-items:center;gap:10px;color:var(--muted);}
.ac-community .ac-sub-tabs-context .ac-mono{color:var(--muted);}
.ac-community .ac-sub-tabs-scenario{font-weight:700;font-size:14px;color:var(--ink);letter-spacing:-.005em;}
.ac-community .ac-sub-tabs{display:inline-flex;align-items:center;gap:4px;padding:4px;background:var(--bg-soft);border-radius:999px;border:1px solid var(--line);}
.ac-community .ac-sub-tab{display:inline-flex;align-items:center;gap:8px;border:none;background:transparent;padding:8px 18px;border-radius:999px;color:var(--muted);font-size:13px;font-weight:600;transition:background .15s ease,color .15s ease;}
.ac-community .ac-sub-tab:hover{color:var(--ink-soft);}
.ac-community .ac-sub-tab.is-active{background:var(--accent);color:#fff;box-shadow:var(--shadow-sm);}
.ac-community .ac-sub-count{font-weight:700;font-size:11px;color:var(--muted);background:var(--bg-card);padding:1px 7px;border-radius:999px;min-width:20px;text-align:center;}
.ac-community .ac-sub-count:empty{display:none;}
.ac-community .ac-sub-tab.is-active .ac-sub-count{background:rgba(255,255,255,.22);color:#fff;}

/* Section heads */
.ac-community .ac-section{margin-top:64px;}
.ac-community .ac-section:first-child{margin-top:0;}
.ac-community .ac-section-head{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:28px;gap:24px;flex-wrap:wrap;}
.ac-community .ac-section-title{font-weight:800;font-size:32px;line-height:1.05;margin:0;letter-spacing:-.02em;color:var(--ink);}
.ac-community .ac-section-title em{color:var(--accent);font-style:normal;font-weight:800;}
.ac-community .ac-section-sub{color:var(--muted);font-size:14px;margin-top:8px;max-width:540px;font-weight:500;}

/* Panes */
.ac-community .ac-pane[hidden],.ac-community .ac-scn-pane[hidden]{display:none!important;}
.ac-community .ac-main{padding:28px 0 16px;}
.ac-community .ac-scn-pane{margin-top:4px;}
.ac-community .ac-html-block{font-size:14px;line-height:1.6;color:var(--ink-soft);}
.ac-community .ac-app-slot-empty{padding:64px 20px;text-align:center;color:var(--muted);border:1px dashed var(--line-strong);border-radius:var(--radius-lg);font-weight:500;}

/* Photo masonry */
.ac-community .ac-masonry{column-count:3;column-gap:24px;}
.ac-community .ac-p-card{break-inside:avoid;margin-bottom:24px;background:var(--bg-card);border:1px solid var(--line);border-radius:var(--radius);overflow:hidden;transition:transform .2s ease,box-shadow .2s ease;}
.ac-community .ac-p-card:hover{transform:translateY(-2px);box-shadow:var(--shadow-md);}
.ac-community .ac-p-img{position:relative;background:var(--bg-soft);}
.ac-community .ac-p-img img{display:block;width:100%;height:auto;}
.ac-community .ac-v-tag{display:inline-flex;align-items:center;gap:6px;background:rgba(15,37,66,.85);backdrop-filter:blur(6px);color:#fff;padding:4px 10px;border-radius:999px;font-weight:600;font-size:11px;letter-spacing:.04em;text-transform:uppercase;white-space:nowrap;}
.ac-community .ac-p-img .ac-v-tag{position:absolute;top:12px;left:12px;}
.ac-community .ac-p-body{padding:18px;}
.ac-community .ac-r-pill{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;background:var(--accent-soft);color:var(--accent-deep);font-weight:600;font-size:11px;letter-spacing:.04em;text-transform:uppercase;}
.ac-community .ac-p-title{font-weight:700;font-size:17px;line-height:1.3;margin:12px 0 8px;letter-spacing:-.005em;color:var(--ink);}
.ac-community .ac-p-excerpt{color:var(--ink-soft);font-size:13.5px;line-height:1.55;margin:0;}
.ac-community .ac-p-foot{display:flex;align-items:center;gap:10px;padding:12px 18px 16px;color:var(--muted);font-size:12.5px;border-top:1px solid var(--line);}
.ac-community .ac-avatar{width:22px;height:22px;border-radius:50%;background:var(--bg-soft);display:grid;place-items:center;font-size:10px;color:var(--ink);border:1px solid var(--line);overflow:hidden;font-weight:700;}
.ac-community .ac-avatar img{width:100%;height:100%;object-fit:cover;}
.ac-community .ac-v-stats{display:inline-flex;gap:12px;font-family:var(--font-mono);font-size:11px;}
.ac-community .ac-p-foot .ac-v-stats{margin-left:auto;color:var(--ink-soft);}
.ac-community .ac-v-stats span{display:inline-flex;align-items:center;gap:4px;}
.ac-community .ac-amb-chip{display:inline-flex;align-items:center;gap:4px;color:var(--accent);font-weight:700;font-size:10.5px;letter-spacing:.04em;text-transform:uppercase;margin-left:2px;}
.ac-community .ac-amb-chip svg{width:12px;height:12px;}

.ac-community .ac-empty{padding:80px 20px;text-align:center;color:var(--muted);border:1px dashed var(--line-strong);border-radius:var(--radius-lg);}
.ac-community .ac-loading{padding:80px 20px;text-align:center;color:var(--muted);font-weight:500;}

/* Responsive */
@media (max-width:1100px){
  .ac-community .ac-hero-grid{grid-template-columns:1fr;gap:24px;}
  .ac-community .ac-masonry{column-count:2;}
}
@media (max-width:700px){
  .ac-community .ac-shell{padding:0 20px;}
  .ac-community .ac-amb-rail .ac-amb-card{flex-basis:82%;}
  .ac-community .ac-masonry{column-count:1;}
  .ac-community .ac-tab{font-size:16px;padding:14px 14px 16px;}
}
`;
    const style = document.createElement("style");
    style.id = "acumen-community-styles";
    style.innerHTML = css;
    document.head.appendChild(style);
  }

  // ─── Utility helpers ────────────────────────────────────
  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    if (attrs) {
      for (const k in attrs) {
        if (k === "class") node.className = attrs[k];
        else if (k === "html") node.innerHTML = attrs[k];
        else if (k.startsWith("on") && typeof attrs[k] === "function") node.addEventListener(k.slice(2), attrs[k]);
        else if (attrs[k] != null) node.setAttribute(k, attrs[k]);
      }
    }
    for (const c of children.flat()) {
      if (c == null || c === false) continue;
      node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    }
    return node;
  }

  function escapeHTML(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
    );
  }
  function initialsOf(name) {
    if (!name) return "AC";
    return name.split(/[\s_]/).filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  }
  function formatCount(n) {
    if (n == null) return "0";
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    return String(n);
  }
  function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso); const diff = Date.now() - d.getTime(); const day = 86400_000;
    if (diff < day) return "today";
    if (diff < 7 * day) return Math.floor(diff / day) + "d ago";
    if (diff < 30 * day) return Math.floor(diff / (7 * day)) + "w ago";
    if (diff < 365 * day) return Math.floor(diff / (30 * day)) + "mo ago";
    return Math.floor(diff / (365 * day)) + "y ago";
  }
  function scenarioTone(id) { return TONE[id] || TONE.daily; }
  function stripeBackground(id) {
    const t = scenarioTone(id);
    return `repeating-linear-gradient(45deg, ${t.a} 0 11px, ${t.b} 11px 22px)`;
  }
  function truncate(s, n) {
    if (!s) return "";
    const flat = String(s).replace(/\s+/g, " ").trim();
    return flat.length > n ? flat.slice(0, n - 1) + "…" : flat;
  }
  function ambassadorChipHTML() {
    return `<span class="ac-amb-chip" title="Acumen Ambassador"><svg viewBox="0 0 12 12" fill="none" width="12" height="12"><circle cx="6" cy="6" r="5.4" fill="currentColor"/><path d="M3.6 6.2l1.7 1.6 3.1-3.4" stroke="#fff" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>Ambassador</span>`;
  }
  function playIconHTML() { return `<svg width="16" height="16" viewBox="0 0 16 16"><path d="M5 3.5v9l7-4.5-7-4.5z" fill="currentColor"/></svg>`; }
  function instagramIconHTML() { return `<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2"/><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor"/></svg>`; }
  function heartIconHTML() { return `<svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 13.5s-5-3.2-5-7a3 3 0 0 1 5-2.2A3 3 0 0 1 13 6.5c0 3.8-5 7-5 7z" stroke="currentColor" stroke-width="1.3"/></svg>`; }
  function commentIconHTML() { return `<svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M2.5 4a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 13.5 4v5A1.5 1.5 0 0 1 12 10.5H6.5L3.5 13V10.5H4A1.5 1.5 0 0 1 2.5 9V4z" stroke="currentColor" stroke-width="1.3"/></svg>`; }

  function labelForScenario(feedEl, id) {
    const btn = feedEl.querySelector(`[data-scenario="${id}"]`);
    return (btn && (btn.dataset.label || btn.textContent.trim())) || id;
  }

  // ─── HTML generators ────────────────────────────────────
  function ambassadorCardHTML(a) {
    const portraitHTML = a.profile_pic_url
      ? `<img src="${escapeHTML(a.profile_pic_url)}" alt="${escapeHTML(a.display_name)}" loading="lazy"/>`
      : `<div style="position:absolute;inset:0;background:${stripeBackground((a.scenarios && a.scenarios[0]) || "daily")};opacity:.55;"></div>`;
    const igUrl = a.instagram_url || ("https://www.instagram.com/" + (a.username || "") + "/");

    let posts = (a.posts || []).slice(0, 3);
    if (!posts.length && store.data && Array.isArray(store.data.posts)) {
      posts = store.data.posts.filter((p) => p.username === a.username).slice(0, 3).map((p) => ({
        thumbnail_url: p.thumbnail_url, media_url: p.media_url, media_type: p.media_type, permalink: p.permalink,
      }));
    }
    const postsHTML = posts.length
      ? `<div class="ac-amb-posts">${posts.map((p) => {
          const img = (p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url) || p.thumbnail_url || p.media_url || "";
          const vid = p.media_type === "VIDEO" ? `<span class="ac-amb-post-vid">${playIconHTML()}</span>` : "";
          return `<a href="${escapeHTML(p.permalink || igUrl)}" target="_blank" rel="noopener">${img ? `<img src="${escapeHTML(img)}" loading="lazy" alt=""/>` : ""}${vid}</a>`;
        }).join("")}</div>`
      : "";

    return `
      <article class="ac-amb-card">
        <div class="ac-amb-portrait">${portraitHTML}<span class="ac-amb-badge-tag">Ambassador</span></div>
        <div>
          <h3 class="ac-amb-name">${escapeHTML(a.display_name || a.username)}</h3>
          ${a.role ? `<span class="ac-amb-role">${escapeHTML(a.role)}</span>` : ""}
        </div>
        ${a.quote ? `<p class="ac-amb-quote">${escapeHTML(a.quote)}</p>` : ""}
        <div class="ac-amb-meta">
          ${a.setup ? `<div class="row"><span class="k">Setup</span><span class="v">${escapeHTML(a.setup)}</span></div>` : ""}
          ${a.base ? `<div class="row"><span class="k">Based</span><span class="v">${escapeHTML(a.base)}</span></div>` : ""}
        </div>
        <div class="ac-amb-foot">
          <span class="clips">${a.clips || 0} clips${a.joined_year ? ` · joined ${a.joined_year}` : ""}</span>
          <a class="follow" href="${escapeHTML(igUrl)}" target="_blank" rel="noopener">${instagramIconHTML()}Follow</a>
        </div>
        ${postsHTML}
      </article>`;
  }

  function postCardHTML(p) {
    const ambHTML = p.is_ambassador ? ambassadorChipHTML() : "";
    const img = p.thumbnail_url || p.media_url || "";
    const imgInner = img
      ? `<img src="${escapeHTML(img)}" loading="lazy" alt=""/>`
      : `<div style="aspect-ratio:4/5;background:${stripeBackground(p.category)};opacity:.55;"></div>`;
    return `
      <article class="ac-p-card">
        <a class="ac-p-img" href="${escapeHTML(p.permalink || "#")}" target="_blank" rel="noopener">
          ${imgInner}
          <span class="ac-v-tag">${escapeHTML(labelFor(p.category))}</span>
        </a>
        <div class="ac-p-body">
          <h3 class="ac-p-title">${escapeHTML(truncate(p.caption || "Untitled post", 80))}</h3>
          ${p.caption ? `<p class="ac-p-excerpt">${escapeHTML(truncate(p.caption, 200))}</p>` : ""}
        </div>
        <div class="ac-p-foot">
          <span class="ac-avatar">${p.profile_pic_url ? `<img src="${escapeHTML(p.profile_pic_url)}" alt=""/>` : escapeHTML(initialsOf(p.display_name || p.username))}</span>
          <div>
            <div style="font-size:13px;color:var(--ink);display:inline-flex;align-items:center;gap:6px;">${escapeHTML(p.display_name || p.username)}${ambHTML}</div>
            <div style="font-size:11px;color:var(--muted);">${escapeHTML(timeAgo(p.timestamp))}</div>
          </div>
          <div class="ac-v-stats">
            <span>${heartIconHTML()} ${formatCount(p.like_count)}</span>
            <span>${commentIconHTML()} ${formatCount(p.comments_count)}</span>
          </div>
        </div>
      </article>`;
  }

  // Scenario label lookup from API data (falls back to id).
  function labelFor(id) {
    const s = (store.data && store.data.scenarios || []).find((x) => x.id === id);
    return (s && s.label) || id;
  }

  // ─── Section: Hero ──────────────────────────────────────
  function initHero(heroEl) {
    onReady(() => {
      const stats = (store.data && store.data.stats) || {};
      const map = {
        clips: formatCount(stats.total_clips || 0),
        curated: formatCount(stats.total_curated || 0),
        ambassadors: String(stats.ambassadors || 0),
      };
      heroEl.querySelectorAll("[data-ac-stat]").forEach((node) => {
        const key = node.getAttribute("data-ac-stat");
        const numEl = node.querySelector(".ac-stat-num");
        if (numEl && map[key] != null) numEl.textContent = map[key];
      });
    });
  }

  // ─── Section: Ambassadors ───────────────────────────────
  function initAmbassadors(ambEl) {
    const rail = ambEl.querySelector("[data-ac-amb-rail]");
    const head = ambEl.querySelector(".ac-amb-head");
    if (!rail) return;
    rail.innerHTML = `<div class="ac-loading" style="flex:1 0 100%;">Loading ambassadors…</div>`;

    onReady(() => {
      const list = store.ambassadors || [];
      const section = ambEl.querySelector(".ac-amb-section") || ambEl;
      if (!list.length) { if (section) section.style.display = "none"; return; }

      rail.innerHTML = list.map(ambassadorCardHTML).join("");

      // Slider arrows (added to the section's head).
      if (head && !head.querySelector(".ac-amb-nav")) {
        const nav = el("div", { class: "ac-amb-nav" });
        const prev = el("button", { "aria-label": "Previous", html: '<svg viewBox="0 0 16 16" fill="none"><path d="M10 3l-5 5 5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' });
        const next = el("button", { "aria-label": "Next", html: '<svg viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' });
        nav.appendChild(prev); nav.appendChild(next); head.appendChild(nav);
        const step = () => Math.max(280, Math.round(rail.clientWidth * 0.85));
        prev.addEventListener("click", () => rail.scrollBy({ left: -step(), behavior: "smooth" }));
        next.addEventListener("click", () => rail.scrollBy({ left: step(), behavior: "smooth" }));
        const upd = () => {
          const scrollable = rail.scrollWidth > rail.clientWidth + 4;
          nav.style.display = scrollable ? "" : "none";
          prev.disabled = rail.scrollLeft <= 2;
          next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
        };
        rail.addEventListener("scroll", upd);
        setTimeout(upd, 0);
      }
    });
  }

  // ─── Section: Feed ──────────────────────────────────────
  // Primary scenario tabs + secondary media tabs.
  //   • Photos & stories → live UGC masonry rendered here.
  //   • Video clips      → per-scenario app/HTML groups pre-rendered by the
  //                         section ([data-ac-video-scn="<scenario>"]).
  function initFeed(feedEl) {
    const scenarioBtns = [...feedEl.querySelectorAll("[data-scenario]")];
    const mediaBtns = [...feedEl.querySelectorAll("[data-media]")];
    const postsHost = feedEl.querySelector("[data-ac-posts]");
    const videoScnPanes = [...feedEl.querySelectorAll("[data-ac-video-scn]")];
    const panes = {
      videos: feedEl.querySelector('[data-ac-pane="videos"]'),
      posts: feedEl.querySelector('[data-ac-pane="posts"]'),
    };
    const scenarioLabelEl = feedEl.querySelector(".ac-sub-tabs-scenario");
    if (!scenarioBtns.length) return;

    let scenario = scenarioBtns[0].dataset.scenario || "all";
    let media = "posts"; // default to live UGC photos

    function showVideoForScenario() {
      let shown = null;
      videoScnPanes.forEach((p) => {
        const on = p.getAttribute("data-ac-video-scn") === scenario;
        p.hidden = !on;
        if (on) shown = p;
      });
      // Placeholder when this scenario has no app/HTML video content.
      if (panes.videos) {
        let ph = panes.videos.querySelector(".ac-app-slot-empty.ac-vid-ph");
        const empty = !shown || !shown.children.length;
        if (empty && !ph) {
          ph = el("div", { class: "ac-shell" });
          ph.innerHTML = `<div class="ac-app-slot-empty ac-vid-ph">No video content for this tab yet — add an app block (e.g. Moast grid) or an HTML block under this scenario in the theme editor.</div>`;
          panes.videos.appendChild(ph);
        }
        if (ph) ph.hidden = !empty;
      }
    }
    function setScenarioActive() {
      scenarioBtns.forEach((b) => b.classList.toggle("is-active", b.dataset.scenario === scenario));
      if (scenarioLabelEl) scenarioLabelEl.textContent = labelForScenario(feedEl, scenario);
      showVideoForScenario();
      renderPosts();
      updateCounts();
    }
    function setMediaActive() {
      mediaBtns.forEach((b) => b.classList.toggle("is-active", b.dataset.media === media));
      if (panes.videos) panes.videos.hidden = media !== "videos";
      if (panes.posts) panes.posts.hidden = media !== "posts";
    }
    function updateCounts() {
      const counts = (store.data && store.data.counts) || {};
      scenarioBtns.forEach((b) => {
        const c = counts[b.dataset.scenario] || {};
        const span = b.querySelector(".count");
        if (span) span.textContent = (c.posts || 0);
      });
      const cur = counts[scenario] || {};
      mediaBtns.forEach((b) => {
        const span = b.querySelector(".ac-sub-count");
        if (!span) return;
        span.textContent = b.dataset.media === "posts" ? String(cur.posts || 0) : "";
      });
    }
    function renderPosts() {
      if (!postsHost) return;
      const data = store.data;
      if (!data) { postsHost.innerHTML = `<div class="ac-loading">Loading…</div>`; return; }
      if (scenario === "all") {
        let html = "";
        scenarioBtns.filter((b) => b.dataset.scenario !== "all").forEach((b) => {
          const id = b.dataset.scenario;
          const items = (data.by_scenario && data.by_scenario[id] && data.by_scenario[id].posts) || [];
          if (!items.length) return;
          html += `<section class="ac-section"><div class="ac-section-head"><div>
              <h2 class="ac-section-title">${escapeHTML(labelForScenario(feedEl, id))}.</h2>
              <div class="ac-section-sub">${escapeHTML(SCENARIO_BLURB[id] || "")}</div>
            </div></div><div class="ac-masonry">${items.map(postCardHTML).join("")}</div></section>`;
        });
        postsHost.innerHTML = html || `<div class="ac-empty">No posts yet — check back soon.</div>`;
      } else {
        const items = (data.by_scenario && data.by_scenario[scenario] && data.by_scenario[scenario].posts) || [];
        postsHost.innerHTML = items.length
          ? `<div class="ac-masonry" style="margin-top:8px;">${items.map(postCardHTML).join("")}</div>`
          : `<div class="ac-empty">No posts in this scenario yet.</div>`;
      }
    }

    scenarioBtns.forEach((b) => b.addEventListener("click", () => { scenario = b.dataset.scenario; setScenarioActive(); }));
    mediaBtns.forEach((b) => b.addEventListener("click", () => { media = b.dataset.media; setMediaActive(); }));

    setScenarioActive();
    setMediaActive();
    onReady(() => { renderPosts(); updateCounts(); });
  }

  // ─── Fetch + boot ───────────────────────────────────────
  async function loadData() {
    try {
      const [communityRes, ambRes] = await Promise.all([
        fetch(BASE + "/api-community"),
        fetch(BASE + "/api-ambassadors"),
      ]);
      if (!communityRes.ok) throw new Error("community " + communityRes.status);
      const community = await communityRes.json();
      const amb = ambRes.ok ? await ambRes.json() : { ambassadors: [] };
      store.data = community;
      store.ambassadors = amb.ambassadors || [];
    } catch (e) {
      console.error("[acumen-community] load failed", e);
      store.error = e;
    }
    store.ready = true;
    subscribers.splice(0).forEach((fn) => { try { fn(); } catch (e) { console.error(e); } });
  }

  function boot() {
    const heroEls = document.querySelectorAll("[data-acumen-hero]");
    const ambEls = document.querySelectorAll("[data-acumen-ambassadors]");
    const feedEls = document.querySelectorAll("[data-acumen-feed]");
    if (!heroEls.length && !ambEls.length && !feedEls.length) return;

    injectFontLink();
    injectStyles();

    heroEls.forEach((n) => { n.classList.add("ac-community"); initHero(n); });
    ambEls.forEach((n) => { n.classList.add("ac-community"); initAmbassadors(n); });
    feedEls.forEach((n) => { n.classList.add("ac-community"); initFeed(n); });

    loadData();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
